import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../../../../../hooks/useNotification";
import {
  useCancelPlannedOrdersMutation,
  useConfirmPlannedOrdersMutation,
  useCreateOrderFromPlannedOrderMutation,
} from "../../../../../store/apiSlice/plannerApiSlice";
import { componentWithCondition } from "../../../../newLayout/page/components/hoc/componentWithConditionCheck";
import { ButtonSpinner } from "../../../../../components/atoms/Button/Button.styles";
import { ListActionBurgerMenu } from "../../../../newLayout/page/components/OptionsForView/ListActionBurgerMenu";
import {
  buildOpenDraftLinks,
  isCancelDisabledForRows,
  isConfirmDisabledForRows,
  isConvertDisabledForRows,
} from "../eligibility";

/**
 * Bulk Confirm / Convert / Cancel for the Planned Orders newLayout list.
 *
 * @param {object} props Component props from OptionsForView.
 * @return {JSX.Element}
 */
const PlannedOrderAction = ({ selectedOrderId, setSelectedOrder, selectedOrderFromListview }) => {
  const notify = useNotification();
  const navigate = useNavigate();
  const [confirmPlannedOrders, { isLoading: isConfirming }] = useConfirmPlannedOrdersMutation();
  const [cancelPlannedOrders, { isLoading: isCancelling }] = useCancelPlannedOrdersMutation();
  const [createOrderFromPlannedOrder, { isLoading: isConverting }] =
    useCreateOrderFromPlannedOrderMutation();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rows = selectedOrderFromListview || [];
  const confirmDisabled = useMemo(() => isConfirmDisabledForRows(rows), [rows]);
  const cancelDisabled = useMemo(() => isCancelDisabledForRows(rows), [rows]);
  const convertDisabled = useMemo(() => isConvertDisabledForRows(rows), [rows]);

  const handleConfirm = async () => {
    if (confirmDisabled || isConfirming) return;
    try {
      const res = await confirmPlannedOrders({ ids: selectedOrderId }).unwrap();
      notify({ type: "success", message: res?.detail || "Planned orders confirmed." });
      setSelectedOrder([]);
    } catch (error) {
      notify({
        type: "error",
        message: error?.data?.detail || "Something went wrong. Please try again.",
      });
    }
  };

  const handleCancel = async () => {
    if (cancelDisabled || isCancelling) return;
    try {
      const res = await cancelPlannedOrders({ ids: selectedOrderId }).unwrap();
      notify({ type: "success", message: res?.detail || "Planned orders cancelled." });
      setSelectedOrder([]);
    } catch (error) {
      notify({
        type: "error",
        message: error?.data?.detail || "Something went wrong. Please try again.",
      });
    }
  };

  const handleConvert = async () => {
    if (convertDisabled || isConverting) return;
    try {
      const res = await createOrderFromPlannedOrder({ ids: selectedOrderId }).unwrap();
      const links = buildOpenDraftLinks(res?.created_orders);
      if (links.length) {
        // Match Receipt / Stock Pack: notify + JSX message (App.jsx → toast).
        notify({
          type: "success",
          message: (
            <div>
              <div>{res?.detail || "Converted to Draft successfully."}</div>
              {links.map(link => (
                <button
                  key={link.path}
                  type="button"
                  className="btn btn-link p-0 d-block text-start text-white text-decoration-underline"
                  onClick={() => navigate(link.path)}
                >
                  Open Draft {link.label}
                </button>
              ))}
            </div>
          ),
        });
      } else {
        notify({
          type: "success",
          message: res?.detail || "Converted to Draft successfully.",
        });
      }
      setSelectedOrder([]);
    } catch (error) {
      notify({
        type: "error",
        message: error?.data?.detail || "Something went wrong. Please try again.",
      });
    }
  };

  const burgerItems = useMemo(
    () => [
      {
        key: "cancel",
        label: "Cancel",
        onClick: handleCancel,
        disabled: cancelDisabled || isCancelling,
        title: cancelDisabled ? "Select planned orders eligible to cancel." : "",
        isLoading: isCancelling,
        isDanger: true,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cancelDisabled, isCancelling, selectedOrderId],
  );

  return (
    <>
      <li className={`tableButton ${!confirmDisabled ? "activeState" : ""}`}>
        <button
          disabled={confirmDisabled || isConfirming}
          onClick={handleConfirm}
          title={confirmDisabled ? "Select planned orders eligible to confirm." : ""}
          style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
        >
          {isConfirming && <ButtonSpinner aria-hidden="true" />}
          {isConfirming ? "Confirming…" : "Confirm"}
        </button>
      </li>
      <li className={`tableButton ${!convertDisabled ? "activeState" : ""}`}>
        <button
          disabled={convertDisabled || isConverting}
          onClick={handleConvert}
          title={convertDisabled ? "Select planned orders eligible to convert." : ""}
          style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
        >
          {isConverting && <ButtonSpinner aria-hidden="true" />}
          {isConverting ? "Converting…" : "Convert"}
        </button>
      </li>
      <ListActionBurgerMenu items={burgerItems} />
    </>
  );
};

export const PlannedOrderActionWithCheck = componentWithCondition(PlannedOrderAction);
